import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { comics, banners, categories, stats } from '@/mocks/comics';

export default function Home() {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  }, []);

  const prevBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [isPaused, nextBanner]);

  const hotComics = [...comics].sort((a, b) => b.views - a.views).slice(0, 8);
  const latestComics = [...comics].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 6);
  const topRanked = [...comics].sort((a, b) => b.rating - a.rating).slice(0, 5);
  const completedComics = comics.filter((c) => c.status === '已完结');

  const currentBannerData = banners[currentBanner];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '韩漫网站',
        url: typeof window !== 'undefined' ? window.location.origin : '',
        description: '韩漫网站是你的专属免费韩漫平台，支持韩漫在线阅读与无删减韩国漫画下拉式浏览，海量免费韩漫大全无需注册就能看。',
        sameAs: [],
      },
      {
        '@type': 'WebSite',
        name: '韩漫网站',
        url: typeof window !== 'undefined' ? window.location.origin : '',
        description: '韩漫在线阅读,免费韩漫大全,无删减韩国漫画,下拉式浏览',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${typeof window !== 'undefined' ? window.location.origin : ''}/search?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '韩漫网站 - 免费韩漫在线阅读平台',
        description: '韩漫网站是你的专属免费韩漫平台，支持韩漫在线阅读与无删减韩国漫画下拉式浏览，海量免费韩漫大全无需注册就能看，各种题材作品一键探索。',
        url: typeof window !== 'undefined' ? window.location.href : '',
        isPartOf: { '@id': typeof window !== 'undefined' ? `${window.location.origin}/#website` : '#website' },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      <main>
        {/* Hero Banner */}
        <section
          className="relative w-full h-[420px] md:h-[560px] overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {banners.map((banner, i) => (
            <Link
              key={banner.id}
              to={`/comic/${banner.comicId}`}
              className={`absolute inset-0 transition-all duration-700 ${
                i === currentBanner
                  ? 'opacity-100 translate-x-0'
                  : i < currentBanner
                  ? 'opacity-0 -translate-x-full'
                  : 'opacity-0 translate-x-full'
              }`}
            >
              <img
                src={banner.image}
                alt={banner.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/70 via-foreground-950/30 to-foreground-950/50"></div>
              <div className="absolute inset-0 flex items-center">
                <div className="px-4 md:px-6 w-full max-w-6xl mx-auto">
                  <div className="max-w-lg">
                    <h2 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight mb-4">
                      {banner.title}
                    </h2>
                    <p className="text-base md:text-lg text-background-200 mb-6">
                      {banner.subtitle}
                    </p>
                    <span className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                      立即阅读
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}

          <button
            onClick={prevBanner}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 hover:bg-background-50/40 transition-colors flex items-center justify-center cursor-pointer"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line text-xl w-5 h-5 flex items-center justify-center"></i>
          </button>
          <button
            onClick={nextBanner}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 hover:bg-background-50/40 transition-colors flex items-center justify-center cursor-pointer"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line text-xl w-5 h-5 flex items-center justify-center"></i>
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentBanner(i)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                  i === currentBanner
                    ? 'bg-primary-500 w-6'
                    : 'bg-background-50/50 hover:bg-background-50/80'
                }`}
                aria-label={`切换到第 ${i + 1} 张`}
              />
            ))}
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-primary-500">
          <div className="px-4 md:px-6 py-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50">{stats.totalComics}</div>
                <div className="text-xs md:text-sm text-background-100/80 mt-1">海量韩漫资源</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50">{stats.totalChapters}</div>
                <div className="text-xs md:text-sm text-background-100/80 mt-1">精彩章节</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50">{stats.dailyReaders}</div>
                <div className="text-xs md:text-sm text-background-100/80 mt-1">每日读者</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50">{stats.satisfaction}</div>
                <div className="text-xs md:text-sm text-background-100/80 mt-1">用户满意度</div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">分类浏览</h2>
              <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap">
                查看全部 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
                >
                  <div className="w-12 h-12 rounded-full bg-background-50 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-xl text-primary-500 w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-500 transition-colors whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">最新更新</h2>
              <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap">
                更多更新 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {latestComics.map((comic, i) => (
                <ComicCard key={comic.id} {...comic} variant="compact" index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* Hot Ranking + Completed */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Hot Ranking */}
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">热门排行</h2>
                  <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                    <button className="px-3 py-1.5 text-xs font-medium rounded-full bg-primary-500 text-background-50 whitespace-nowrap cursor-pointer">周榜</button>
                    <button className="px-3 py-1.5 text-xs font-medium rounded-full text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">月榜</button>
                    <button className="px-3 py-1.5 text-xs font-medium rounded-full text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">总榜</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                  {hotComics.map((comic, i) => (
                    <ComicCard key={comic.id} {...comic} variant="default" index={i} />
                  ))}
                </div>
              </div>

              {/* Sidebar */}
              <div>
                {/* Top Rated */}
                <div className="mb-8">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">高分推荐</h3>
                  <div className="space-y-3">
                    {topRanked.map((comic) => (
                      <ComicCard key={comic.id} {...comic} variant="featured" />
                    ))}
                  </div>
                </div>

                {/* Completed Works */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg font-bold text-foreground-900">已完结佳作</h3>
                  </div>
                  <div className="space-y-2">
                    {completedComics.map((comic) => (
                      <Link
                        key={comic.id}
                        to={`/comic/${comic.slug}`}
                        className="flex items-center gap-3 p-2 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                      >
                        <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-double-line text-secondary-500 w-4 h-4 flex items-center justify-center"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground-800 line-clamp-1">{comic.title}</p>
                          <div className="flex items-center gap-2 text-xs text-foreground-500">
                            <span>{comic.author}</span>
                            <span>·</span>
                            <span className="text-accent-500">{comic.rating}分</span>
                          </div>
                        </div>
                        <span className="text-[10px] px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap">已完结</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* All Comics Showcase */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8">推荐韩漫</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {comics.map((comic) => (
                <ComicCard key={comic.id} {...comic} variant="default" />
              ))}
            </div>
            <div className="flex justify-center mt-10">
              <Link
                to="/category/all"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                浏览全部韩漫
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-12">
              为什么选择韩漫网站？
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-accent-100 flex items-center justify-center">
                  <i className="ri-book-open-line text-2xl text-accent-500 w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">海量资源</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">收录超过12000部韩国漫画，涵盖恋爱、奇幻、动作、悬疑等多种题材，应有尽有。</p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-speed-line text-2xl text-primary-500 w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">高速更新</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">热门韩漫每日更新，第一时间同步最新章节，让你永远走在追更的最前线。</p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-secondary-100 flex items-center justify-center">
                  <i className="ri-smartphone-line text-2xl text-secondary-500 w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">下拉式阅读</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">专为手机优化的下拉式浏览体验，无需翻页，轻松滑动即可沉浸阅读。</p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-accent-100 flex items-center justify-center">
                  <i className="ri-shield-check-line text-2xl text-accent-500 w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">无删减内容</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">所有韩漫均为无删减完整版，原汁原味呈现韩国漫画的精彩内容与精美画风。</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">常见问题</h2>
            <div className="space-y-4">
              {[
                {
                  q: '韩漫网站是免费的吗？',
                  a: '是的，韩漫网站上的所有韩国漫画都是免费阅读的，无需注册或付费，打开就能看。',
                },
                {
                  q: '如何搜索我想看的韩漫？',
                  a: '你可以在页面顶部的搜索框中输入漫画名称或作者名字，也可以按分类浏览找到感兴趣的作品。',
                },
                {
                  q: '支持手机阅读吗？',
                  a: '当然支持！韩漫网站采用下拉式浏览设计，专门为手机端优化了阅读体验，滑动即可阅读，非常方便。',
                },
                {
                  q: '漫画更新频率如何？',
                  a: '热门连载韩漫通常每日或隔日更新，我们会第一时间同步最新的韩漫章节。',
                },
              ].map((faq, i) => (
                <details key={i} className="bg-background-50 rounded-xl border border-background-200 overflow-hidden group cursor-pointer">
                  <summary className="flex items-center justify-between px-6 py-4 text-sm font-medium text-foreground-800 list-none">
                    {faq.q}
                    <i className="ri-add-line text-foreground-400 group-open:hidden w-4 h-4 flex items-center justify-center"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block w-4 h-4 flex items-center justify-center"></i>
                  </summary>
                  <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}